import { visuallyHidden } from '@mui/utils';
import { useLayoutEffect, useRef, useState } from 'react';

type Listener = (text: string) => void;

interface Region {
  listener: Listener;
  exposed: () => boolean;
}

// Mount order: the app root registers first, any dialog region after it.
const regions = new Set<Region>();

// How often to check whether an aria-hidden ancestor has cleared.
const HIDDEN_RETRY_MS = 100;

/**
 * Speaks `text` through a mounted `LiveRegion`. A region that is exposed right now speaks at
 * once, so a count filtered inside an open dialog is heard there and then. With every region
 * hidden (a dialog on its way out), the root region holds the text until it is exposed again.
 * Toasts route through this because a live region that already exists when its text changes is
 * announced far more reliably than an alert element that mounts and unmounts.
 */
export const announce = (text: string): void => {
  const trimmed = text.trim();
  if (!trimmed) return;
  const all = [...regions];
  const target = all.find((region) => region.exposed()) ?? all[0];
  target.listener(trimmed);
};

/**
 * Announces `text` whenever it changes after the first render, for visible status text such as
 * result counts that sighted users see update in place.
 */
export const useAnnounceChange = (text: string): void => {
  const previous = useRef<string>();
  // During commit, so the announcement is queued in the same tick as the visible change.
  useLayoutEffect(() => {
    if (previous.current !== undefined && previous.current !== text) announce(text);
    previous.current = text;
  }, [text]);
};

/**
 * A persistent, visually hidden polite live region. Mount once at the app root and once inside
 * each dialog. Each announcement renders as a fresh node, so repeating the same text is
 * announced again. An inactive region (its dialog is closing) takes no announcements.
 */
export const LiveRegion = ({ active = true }: { active?: boolean }) => {
  const [message, setMessage] = useState<{ texts: string[]; nonce: number }>({
    texts: [],
    nonce: 0,
  });
  const ref = useRef<HTMLDivElement>(null);
  // Read at announce time, so a dialog region stops taking announcements as soon as its dialog
  // starts closing, before its exit transition unmounts it.
  const activeRef = useRef(active);
  activeRef.current = active;

  // Subscribes during commit, so a toast that mounts in the same frame is not missed.
  useLayoutEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let pending: string[] = [];
    // An open MUI dialog leaves everything else aria-hidden until its exit transition ends,
    // and a toast fired from the dialog, or by its closing mutation, lands inside that window,
    // where screen readers ignore it. Hold the text until the region is exposed again, however
    // long the dialog stays open; anything announced meanwhile joins the queue and is spoken
    // with it.
    const flush = () => {
      if (ref.current?.closest('[aria-hidden="true"]') != null) {
        timer = setTimeout(flush, HIDDEN_RETRY_MS);
        return;
      }
      const texts = pending;
      pending = [];
      timer = undefined;
      setMessage((prev) => ({ texts, nonce: prev.nonce + 1 }));
    };
    // Deferred a tick so announcements made together, such as two toasts entering at once,
    // render as one message instead of the last one replacing the rest.
    const listener: Listener = (text) => {
      pending.push(text);
      timer ??= setTimeout(flush, 0);
    };
    const region: Region = {
      listener,
      exposed: () => activeRef.current && ref.current?.closest('[aria-hidden="true"]') == null,
    };
    regions.add(region);
    return () => {
      clearTimeout(timer);
      regions.delete(region);
    };
  }, []);

  // One node per announcement: a screen reader pauses between elements as it would between
  // sentences, which a joined string would not give it.
  return (
    <div aria-atomic="true" aria-live="polite" ref={ref} role="status" style={visuallyHidden}>
      {message.texts.map((text, index) => (
        <p key={`${message.nonce}-${index}`} style={{ margin: 0 }}>
          {text}
        </p>
      ))}
    </div>
  );
};
